import { FormControl, type FormControlProps, FormErrorMessage, FormLabel } from "@chakra-ui/react";
import { type MultiValue, type Options, Select as ReactSelect, type SingleValue } from "chakra-react-select";
import type { ReactElement } from "react";
import { Text } from "../Text";

export type TOption<T = unknown> = {
  label: string;
  value: T;
};

export interface SelectProps<T> {
  name: string;
  label?: string;
  /** Accessible name for a select shown without a visible label. */
  "aria-label"?: string;
  error?: string;
  isRequired?: boolean;
  isInvalid?: boolean;
  isDisabled?: boolean;
  options?: Options<TOption>;
  defaultValue?: TOption<T[keyof T]>;
  controlProps?: FormControlProps;
  isMulti?: false;
  placeholder?: string;
  value?: TOption<T[keyof T]>;
  onChange: (newValue: SingleValue<TOption<T[keyof T]>>) => void;
}

export interface MultiSelectProps<T> extends Omit<SelectProps<T>, "onChange" | "isMulti" | "value" | "defaultValue"> {
  isMulti: true;
  /** react-select closes after every pick by default; false keeps it open until the user clicks away. */
  closeMenuOnSelect?: boolean;
  value?: TOption<T[keyof T]>[];
  defaultValue?: TOption<T[keyof T]>[];
  onChange: (newValue: MultiValue<TOption<T[keyof T]>>) => void;
}

export const Select = <T = unknown>({
  name,
  label,
  isRequired = false,
  isInvalid = false,
  isDisabled = false,
  error,
  options = [],
  controlProps = { mb: 6 },
  ...rest
}: SelectProps<T> | MultiSelectProps<T>): ReactElement => {
  return (
    <FormControl isRequired={isRequired} isInvalid={isInvalid} isDisabled={isDisabled} {...controlProps}>
      {label && <FormLabel as={Text}>{label}</FormLabel>}
      <ReactSelect name={name} isInvalid={isInvalid} isDisabled={isDisabled} options={options} {...rest} />
      {error && <FormErrorMessage>{error}</FormErrorMessage>}
    </FormControl>
  );
};

Select.displayName = "Select";
